import { useState, useEffect, useMemo } from 'react'
import { Plus, Pencil, Trash2, Search, X, Check, Eye, EyeOff, RotateCcw } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast, ConfirmModal } from '../../../components/Toast'

interface User {
  id: string
  email: string
  username: string
  first_name: string | null
  last_name: string | null
  phone: string | null
  is_active: boolean
  role: string
  module_permissions: Record<string, string[]> | null
  created_at: string
}

interface UserForm {
  email: string
  first_name: string
  last_name: string
  phone: string
  password: string
  is_active: boolean
  role: string
  module_permissions: Record<string, string[]>
}

// Moduli e azioni dal backend permissions.py
const MODULES = [
  { key: 'offerte', label: 'Offerte' },
  { key: 'commesse', label: 'Commesse' },
  { key: 'schede', label: 'Schede' },
  { key: 'anagrafiche', label: 'Anagrafiche' },
  { key: 'magazzino', label: 'Magazzino' },
  { key: 'fascicoli', label: 'Fascicoli' },
  { key: 'calendario', label: 'Calendario' },
  { key: 'scadenziario', label: 'Scadenziario' },
  { key: 'configurazione', label: 'Configurazione' },
]

const ACTIONS = [
  { key: 'view', label: 'Visualizza' },
  { key: 'create', label: 'Crea' },
  { key: 'edit', label: 'Modifica' },
  { key: 'delete', label: 'Elimina' },
  { key: 'approve', label: 'Approva' },
  { key: 'send', label: 'Invia' },
]

const ROLES = [
  { value: 'admin', label: 'Admin', color: 'bg-purple-100 text-purple-700' },
  { value: 'manager', label: 'Manager', color: 'bg-blue-100 text-blue-700' },
  { value: 'user', label: 'Utente', color: 'bg-gray-100 text-gray-700' },
  { value: 'readonly', label: 'Sola Lettura', color: 'bg-amber-100 text-amber-700' },
]

// Permessi default per ruolo (mirror di permissions.py)
const ROLE_DEFAULT_PERMISSIONS: Record<string, Record<string, string[]>> = {
  admin: {
    offerte: ['view', 'create', 'edit', 'delete', 'approve', 'send'],
    commesse: ['view', 'create', 'edit', 'delete', 'approve'],
    schede: ['view', 'create', 'edit', 'delete', 'approve'],
    anagrafiche: ['view', 'create', 'edit', 'delete'],
    magazzino: ['view', 'create', 'edit', 'delete'],
    fascicoli: ['view', 'create', 'edit', 'delete'],
    calendario: ['view', 'create', 'edit', 'delete'],
    scadenziario: ['view', 'create', 'edit', 'delete'],
    configurazione: ['view', 'edit'],
  },
  manager: {
    offerte: ['view', 'create', 'edit', 'approve', 'send'],
    commesse: ['view', 'create', 'edit', 'approve'],
    schede: ['view', 'create', 'edit', 'approve'],
    anagrafiche: ['view', 'create', 'edit'],
    magazzino: ['view', 'create', 'edit'],
    fascicoli: ['view', 'create', 'edit'],
    calendario: ['view', 'create', 'edit'],
    scadenziario: ['view', 'create', 'edit'],
    configurazione: ['view'],
  },
  user: {
    offerte: ['view', 'create', 'edit', 'send'],
    commesse: ['view', 'create', 'edit'],
    schede: ['view', 'create', 'edit'],
    anagrafiche: ['view', 'create', 'edit'],
    magazzino: ['view'],
    fascicoli: ['view', 'create', 'edit'],
    calendario: ['view', 'create', 'edit'],
    scadenziario: ['view'],
    configurazione: ['view'],
  },
  readonly: {
    offerte: ['view'],
    commesse: ['view'],
    schede: ['view'],
    anagrafiche: ['view'],
    magazzino: ['view'],
    fascicoli: ['view'],
    calendario: ['view'],
    scadenziario: ['view'],
    configurazione: ['view'],
  },
}

const emptyForm: UserForm = {
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  password: '',
  is_active: true,
  role: 'user',
  module_permissions: {},
}

export default function UtentiPage() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [form, setForm] = useState<UserForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPermissions, setShowPermissions] = useState(false)

  // Toast e Confirm
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<User | null>(null)

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await api.get('/v1/anagrafiche/utenti')
      setUsers(response.data)
    } catch (err) {
      console.error('Errore caricamento utenti:', err)
      setToast({ message: 'Errore nel caricamento degli utenti', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // Calcola i permessi effettivi (default del ruolo + override custom)
  const effectivePermissions = useMemo(() => {
    const defaults = ROLE_DEFAULT_PERMISSIONS[form.role] || ROLE_DEFAULT_PERMISSIONS['readonly']
    if (!form.module_permissions || Object.keys(form.module_permissions).length === 0) {
      return defaults
    }
    // Override: per ogni modulo con override custom usa quelli, altrimenti default
    const result = { ...defaults }
    for (const [mod, actions] of Object.entries(form.module_permissions)) {
      if (Array.isArray(actions)) {
        result[mod] = actions
      }
    }
    return result
  }, [form.role, form.module_permissions])

  const hasCustomPermissions = Object.keys(form.module_permissions).length > 0

  const togglePermission = (module: string, action: string) => {
    const current = form.module_permissions[module] || ROLE_DEFAULT_PERMISSIONS[form.role]?.[module] || []
    const newActions = current.includes(action)
      ? current.filter(a => a !== action)
      : [...current, action]
    setForm({
      ...form,
      module_permissions: { ...form.module_permissions, [module]: newActions }
    })
  }

  const resetPermissions = () => {
    setForm({ ...form, module_permissions: {} })
  }

  const handleCreate = () => {
    setEditingUser(null)
    setForm(emptyForm)
    setError('')
    setShowPassword(false)
    setShowPermissions(false)
    setShowModal(true)
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)
    setForm({
      email: user.email,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      password: '',
      is_active: user.is_active,
      role: user.role,
      module_permissions: user.module_permissions || {},
    })
    setError('')
    setShowPassword(false)
    setShowPermissions(false)
    setShowModal(true)
  }

  const handleDeleteClick = (user: User) => {
    setConfirmDelete(user)
  }

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return

    try {
      await api.delete(`/v1/anagrafiche/utenti/${confirmDelete.id}`)
      setToast({ message: 'Utente eliminato con successo', type: 'success' })
      loadUsers()
    } catch (err) {
      console.error('Errore eliminazione:', err)
      setToast({ message: 'Errore durante l\'eliminazione', type: 'error' })
    } finally {
      setConfirmDelete(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      const data: Record<string, unknown> = {
        email: form.email,
        username: form.email,
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        phone: form.phone || null,
        is_active: form.is_active,
        role: form.role,
        module_permissions: Object.keys(form.module_permissions).length > 0 ? form.module_permissions : null,
      }

      if (form.password) {
        data.password = form.password
      }

      if (editingUser) {
        await api.put(`/v1/anagrafiche/utenti/${editingUser.id}`, data)
        setToast({ message: 'Utente modificato con successo', type: 'success' })
      } else {
        if (!form.password) {
          setError('La password è obbligatoria per i nuovi utenti')
          setSaving(false)
          return
        }
        await api.post('/v1/anagrafiche/utenti', data)
        setToast({ message: 'Utente creato con successo', type: 'success' })
      }

      setShowModal(false)
      loadUsers()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      setError(error.response?.data?.detail || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const filteredUsers = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    (u.first_name?.toLowerCase() || '').includes(search.toLowerCase()) ||
    (u.last_name?.toLowerCase() || '').includes(search.toLowerCase())
  )

  const getRoleBadge = (role: string) => {
    const r = ROLES.find(r => r.value === role) || ROLES[2]
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${r.color}`}>
        {r.label}
      </span>
    )
  }

  return (
    <div>
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Elimina Utente"
        message={`Sei sicuro di voler eliminare "${confirmDelete?.email}"?`}
        confirmText="Elimina"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
        variant="danger"
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Utenti</h1>
          <p className="text-gray-500">Gestione utenti dell'azienda</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nuovo Utente
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca utenti..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {search ? 'Nessun utente trovato' : 'Nessun utente presente'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefono</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ruolo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">
                      {user.first_name} {user.last_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{user.email}</td>
                  <td className="px-6 py-4 text-gray-600">{user.phone || '-'}</td>
                  <td className="px-6 py-4">
                    {getRoleBadge(user.role)}
                    {user.module_permissions && Object.keys(user.module_permissions).length > 0 && (
                      <span className="ml-1 text-xs text-amber-600" title="Permessi personalizzati">*</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`flex items-center gap-1 text-sm ${
                      user.is_active ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {user.is_active ? (
                        <><Check className="w-4 h-4" /> Attivo</>
                      ) : (
                        <><X className="w-4 h-4" /> Disabilitato</>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(user)}
                        className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Modifica"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(user)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-xl shadow-xl mx-4 max-h-[90vh] overflow-y-auto ${showPermissions ? 'w-full max-w-4xl' : 'w-full max-w-lg'}`}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingUser ? 'Modifica Utente' : 'Nuovo Utente'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome
                  </label>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cognome
                  </label>
                  <input
                    type="text"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefono
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password {editingUser ? '(lascia vuoto per non modificare)' : '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required={!editingUser}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    style={{ fontSize: '16px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ruolo
                  </label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value, module_permissions: {} })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center pt-7">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Utente attivo</span>
                  </label>
                </div>
              </div>

              {/* Toggle permessi avanzati */}
              <div className="border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPermissions(!showPermissions)}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  {showPermissions ? 'Nascondi permessi avanzati' : 'Mostra permessi avanzati'}
                </button>
                {form.role === 'admin' && (
                  <p className="text-xs text-gray-400 mt-1">Admin ha sempre tutti i permessi (non modificabili)</p>
                )}
              </div>

              {/* Matrice permessi */}
              {showPermissions && form.role !== 'admin' && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b border-gray-200">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Permessi per modulo</span>
                      {hasCustomPermissions && (
                        <span className="ml-2 text-xs text-amber-600">(personalizzati)</span>
                      )}
                    </div>
                    {hasCustomPermissions && (
                      <button
                        type="button"
                        onClick={resetPermissions}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary-600"
                      >
                        <RotateCcw className="w-3 h-3" />
                        Ripristina default
                      </button>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-2 text-left font-medium text-gray-600">Modulo</th>
                          {ACTIONS.map(a => (
                            <th key={a.key} className="px-3 py-2 text-center font-medium text-gray-600 whitespace-nowrap">
                              {a.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {MODULES.map(mod => {
                          const defaultPerms = ROLE_DEFAULT_PERMISSIONS[form.role]?.[mod.key] || []
                          const customPerms = form.module_permissions[mod.key]
                          const activePerms = effectivePermissions[mod.key] || []

                          return (
                            <tr key={mod.key} className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium text-gray-700">{mod.label}</td>
                              {ACTIONS.map(action => {
                                const isActive = activePerms.includes(action.key)
                                const isDefault = defaultPerms.includes(action.key)
                                const isCustom = customPerms !== undefined

                                return (
                                  <td key={action.key} className="px-3 py-2 text-center">
                                    <label className="inline-flex items-center justify-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isActive}
                                        onChange={() => togglePermission(mod.key, action.key)}
                                        className={`w-4 h-4 rounded border-gray-300 focus:ring-primary-500 ${
                                          isCustom && isActive !== isDefault
                                            ? 'text-amber-600'
                                            : 'text-primary-600'
                                        }`}
                                      />
                                    </label>
                                  </td>
                                )
                              })}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
                    <p className="text-xs text-gray-400">
                      Grigio = default del ruolo. Se modifichi un modulo, i permessi diventano personalizzati (arancione).
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
